import React from "react";
import Switch from "../../Components/common/Switch";

export const WEEK_DAYS = [
  { key: "sunday", labelKey: "sunday" },
  { key: "monday", labelKey: "monday" },
  { key: "tuesday", labelKey: "tuesday" },
  { key: "wednesday", labelKey: "wedensday" },
  { key: "thursday", labelKey: "thursday" },
  { key: "friday", labelKey: "friday" },
  { key: "saturday", labelKey: "saturday" },
];

const DEFAULT_FROM = "9:00 AM";
const DEFAULT_TO = "6:00 PM";

export const buildTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    [0, 30].forEach((minute) => {
      const period = hour < 12 ? "AM" : "PM";
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      const mm = minute === 0 ? "00" : "30";
      options.push(`${hour12}:${mm} ${period}`);
    });
  }
  return options;
};

const TIME_OPTIONS = buildTimeOptions();

export const getDefaultDayHours = () => ({
  open: true,
  from: DEFAULT_FROM,
  to: DEFAULT_TO,
});

export const getDefaultWorkingHours = () =>
  WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = getDefaultDayHours();
    return acc;
  }, {});

const normalizeDay = (value) => {
  const defaults = getDefaultDayHours();
  if (!value || typeof value !== "object") return defaults;
  return {
    open: value.open !== false,
    from:
      typeof value.from === "string" && value.from.trim()
        ? value.from
        : defaults.from,
    to:
      typeof value.to === "string" && value.to.trim()
        ? value.to
        : defaults.to,
  };
};

export const parseWorkingHours = (value) => {
  let parsed = value;
  if (typeof value === "string" && value.trim()) {
    try {
      parsed = JSON.parse(value);
    } catch (e) {
      parsed = null;
    }
  }
  const defaults = getDefaultWorkingHours();
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return defaults;
  }
  return WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = normalizeDay(parsed[day.key]);
    return acc;
  }, {});
};

export const formatWorkingHoursSummary = (value, translate) => {
  const hours = parseWorkingHours(value);
  return WEEK_DAYS.map((day) => {
    const dayHours = hours[day.key];
    const dayLabel = translate ? translate(day.labelKey) : day.key;
    if (!dayHours.open) {
      return `${dayLabel}: ${translate ? translate("closed") : "Closed"}`;
    }
    return `${dayLabel}: ${dayHours.from} — ${dayHours.to}`;
  }).join(", ");
};

const TimeSelect = ({ value, onChange, disabled }) => {
  const options =
    value && !TIME_OPTIONS.includes(value)
      ? [value, ...TIME_OPTIONS]
      : TIME_OPTIONS;

  return (
    <select
      value={value}
      disabled={disabled}
      onChange={onChange}
      style={{
        border: "none",
        borderBottom: "1px solid currentColor",
        background: "transparent",
        color: "inherit",
        fontSize: 13,
        minWidth: 110,
        padding: "4px 2px",
        outline: "none",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {options.map((time) => (
        <option key={time} value={time}>
          {time}
        </option>
      ))}
    </select>
  );
};

export const WorkingHoursSelector = ({ value, onChange, translate }) => {
  const hours = parseWorkingHours(value);

  const updateDay = (dayKey, field, fieldValue) => {
    onChange({
      ...hours,
      [dayKey]: {
        ...hours[dayKey],
        [field]: fieldValue,
      },
    });
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        {translate("workingHours")}
      </div>
      {WEEK_DAYS.map((day) => {
        const dayHours = hours[day.key];
        return (
          <div
            key={day.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <div style={{ minWidth: 90, fontSize: 13 }}>
              {translate(day.labelKey)}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: 90,
              }}
            >
              <Switch
                size="small"
                checked={!!dayHours.open}
                onChange={(e) =>
                  updateDay(day.key, "open", e.target.checked)
                }
                color="primary"
              />
              <span style={{ fontSize: 13 }}>{translate("open")}</span>
            </div>
            <TimeSelect
              value={dayHours.from}
              disabled={!dayHours.open}
              onChange={(e) => updateDay(day.key, "from", e.target.value)}
            />
            <span style={{ opacity: 0.7 }}>—</span>
            <TimeSelect
              value={dayHours.to}
              disabled={!dayHours.open}
              onChange={(e) => updateDay(day.key, "to", e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
};
